<template>
  <vs-card class="mb-4 signer-card">
    <div class="vx-row">
      <!-- Manejador de arrastre y número de orden -->
      <div class="vx-col w-full mb-2">
        <div class="signer-header flex items-center">
          <div class="drag-handle">
            <feather-icon
              icon="MenuIcon"
              class="cursor-move mr-2"
              svgClasses="w-5 h-5 text-primary"
            ></feather-icon>
            <span class="signer-order text-xl font-bold">Firmante {{ index + 1 }}</span>
          </div>
          <vs-button
            type="flat"
            color="danger"
            icon-pack="feather"
            icon="icon-x"
            class="ml-auto"
            @click="$emit('remove-signer', index)"
          ></vs-button>
        </div>
      </div>

      <!-- Correo electrónico -->
      <div class="vx-col w-full mb-2">
        <vue-simple-suggest
          ref="auto_suggest"
          placeholder="Correo electrónico"
          v-model="signer.email"
          :list="emailSuggestions"
          @input="onInputChange"
          @select="onSelectSuggestion"
          :min-length="3"
        >
          <vs-input
            :danger="focusValidation && (!$v.signer.email.email || !$v.signer.email.required)"
            val-icon-danger="icon-x"
            val-icon-pack="feather"
            @blur="validationEval"
            @focus="focusValidation = true"
            class="w-full"
            label-placeholder="Correo electrónico"
            v-model.trim="signer.email"
          />
        </vue-simple-suggest>
        <div class="text-danger" v-if="focusValidation && !$v.signer.email.required">
          El campo no puede estar vacío
        </div>
        <div class="text-danger" v-if="focusValidation && !$v.signer.email.email">
          El correo es inválido
        </div>
      </div>

      <!-- Nombre -->
      <div class="vx-col w-full mb-2">
        <vs-input
          :danger="focusValidation && !$v.signer.name.required"
          val-icon-danger="icon-x"
          val-icon-pack="feather"
          @blur="validationEval"
          @focus="focusValidation = true"
          class="w-full"
          label-placeholder="Nombre completo"
          v-model.trim="signer.name"
        />
        <div class="text-danger" v-if="focusValidation && !$v.signer.name.required">
          El campo no puede estar vacío
        </div>
      </div>

      <!-- Restricción por RFC o CURP -->
      <div class="vx-col w-full mb-2">
        <vx-input-group>
          <div class="flex">
            Restringe la firma por RFC o CURP
            <vx-tooltip class="d-inline-block" text="Más información" position="top">
              <a target="_blank" href="https://ayuda.verificamex.com/article/61-restringir-con-rfc-o-curp/">
                <feather-icon
                  icon="HelpCircleIcon"
                  class="top-1 mx-2 text-primary"
                  svgClasses="w-5 h-5 text-primary"
                ></feather-icon>
              </a>
            </vx-tooltip>
            <vs-switch v-model="signer.restrictSign" @change="onRestrictSignChange" />
          </div>

          <div class="mt-4 mb-4" v-if="signer.restrictSign">
            <p class="mb-3">
              Solo el firmante con el RFC o CURP que ingreses podrá firmar el documento.
            </p>
            <vs-radio
              class="mr-4"
              v-model="signer.restrictSelector"
              vs-value="RFC"
              :vs-name="'restrict-type-' + index"
              @change="onRestrictSelectorChange"
            >
              RFC (e.firma SAT)
            </vs-radio>
            <vs-radio 
              v-model="signer.restrictSelector"
              vs-value="CURP"
              :vs-name="'restrict-type-' + index"
              @change="onRestrictSelectorChange"
            >
              CURP (Firma con FIREL)
            </vs-radio>

            <!-- Campo RFC o CURP -->
            <vs-input
              :danger="!rfcOrCurpValid"
              val-icon-danger="icon-x"
              val-icon-pack="feather"
              class="w-full mt-5"
              :label-placeholder="signer.restrictSelector === 'RFC' ? 'Restringir con RFC. Solo para e.firma SAT.' : 'Restringir con CURP. Solo para Firma Electrónica Simple y FIREL.'"
              v-model.trim="signer.rfc"
            />
            <div class="text-danger" v-if="!rfcOrCurpValid">
              {{ signer.restrictSelector === 'RFC' ? 'El RFC es inválido' : 'El CURP es inválido' }}
            </div>
          </div>
        </vx-input-group>

        <vx-input-group>
          <div class="vx-col w-full mb-2">
            <!-- Selector de teléfono con código de país -->
            <div class="phone-input-container">
              <label class="text-sm text-gray-600 mb-1 block">Notificación por WhatsApp (opcional)</label>
              <vue-tel-input
                v-model="phoneDisplay"
                @input="onPhoneInput"
                :inputOptions="telInputOptions.inputOptions"
                :defaultCountry="telInputOptions.defaultCountry"
                :enabledFlags="telInputOptions.enabledFlags"
                :enabledCountryCode="telInputOptions.enabledCountryCode"
                :preferredCountries="telInputOptions.preferredCountries"
                :onlyCountries="telInputOptions.onlyCountries"
                :invalidMsg="telInputOptions.invalidMsg"
                :mode="telInputOptions.mode"
                :autocomplete="'tel'"
                :inputClasses="{'input-error': !phoneValid}"
                :dropdownOptions="{showDialCodeInSelection: true}"
                class="vue-tel-input-custom"
              />
            </div>
            <div class="text-danger" v-if="!phoneValid">
              El número de teléfono es inválido
            </div>
            <small class="mb-0 mt-2 block">
              Puedes agregar el teléfono del firmante para notificar mediante Whatsapp.
            </small>
          </div>
        </vx-input-group>
      </div>
    </div>
  </vs-card>
</template>

<script>
import {required, email, helpers} from 'vuelidate/lib/validators';
import VueSimpleSuggest from 'vue-simple-suggest';
import { VueTelInput } from 'vue-tel-input'; // Importamos la librería
import 'vue-simple-suggest/dist/styles.css';
import {throttle} from "lodash";

const rfcRegexValidation = helpers.regex(
  'rfcRegexValidation',
  /^([A-ZÑ&]{3,4})(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01]))([A-Z\d]{2})([A\d])$/
);
const curpRegexValidation = helpers.regex(
  'curpRegexValidation',
  /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/
);

export default {
  name: 'SignerCard',
  props: {
    signer: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    sortable: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    VueSimpleSuggest,
    VueTelInput // Registramos el componente
  },
  data() {
    return {
      focusValidation: false,
      emailSuggestions: [],
      phoneDisplay: '', // Para mostrar el teléfono formateado
      phoneData: {
        number: '',
        isValid: true,
        countryCode: '+52', // Código de México por defecto
        formatted: ''
      },

      // Opciones para vue-tel-input
      telInputOptions: {
        mode: 'international',
        defaultCountry: 'MX', // México por defecto
        disabledFetchingCountry: false,
        enabledCountryCode: true,
        enabledFlags: true,
        preferredCountries: ['MX', 'US', 'ES', 'CO', 'AR', 'CL', 'PE'],
        onlyCountries: [], // Dejar vacío para incluir todos los países
        inputOptions: {
          placeholder: 'Ingrese el número de teléfono',
          showDialCode: true,
        },
        invalidMsg: 'Número de teléfono inválido'
      }
    };
  },
  created() {
    this.debouncedSearch = throttle(this.searchSigners, 1000);

    // Si ya hay un número de teléfono, lo configuramos para el componente
    if (this.signer.phone) {
      // Simplemente asignamos el valor para que el componente lo maneje
      this.phoneDisplay = this.signer.phone;
    }
  },
  computed: {
    rfcOrCurpValid() {
      if (!this.signer.restrictSign) return true;
      if (this.signer.restrictSelector === 'RFC') {
        return rfcRegexValidation(this.signer.rfc);
      } else {
        return curpRegexValidation(this.signer.rfc);
      }
    },
    phoneValid() {
      return this.phoneData.isValid;
    }
  },
  validations: {
    signer: {
      name: {
        required,
      },
      email: {
        required,
        email,
      },
      rfc: {
        rfcOrCurpValid(value) {
          if (!this.signer.restrictSign) return true;
          if (this.signer.restrictSelector === 'RFC') {
            return rfcRegexValidation(value);
          } else {
            return curpRegexValidation(value);
          }
        },
      },
    },
  },
  methods: {
    // Método para manejar el cambio en el input del teléfono
    onPhoneInput(formattedNumber, { number, isValid, country }) {
      // Validación manual de respaldo para México (10 dígitos después del +52)
      // Esto es necesario para claves LADA nuevas como la 220 que la librería vieja no reconoce
      let manualValid = isValid;
      
      if (!isValid && country && country.iso2 === 'MX') {
        // Limpiamos el número para quedarnos solo con dígitos
        const digitsOnly = formattedNumber.replace(/\D/g, '');
        // Si tiene 12 dígitos (52 + 10 del número) o 10 dígitos (sin el 52)
        if (digitsOnly.length === 12 && digitsOnly.startsWith('52')) {
          manualValid = true;
        } else if (digitsOnly.length === 10) {
          manualValid = true;
        }
      }

      this.phoneData = {
        number: number,
        countryCode: country && country.dialCode ? `+${country.dialCode}` : null,
        isValid: manualValid,
        formatted: formattedNumber
      };

      // Actualizamos el valor en el objeto signer
      if (manualValid) {
        this.$set(this.signer, 'phone', this.phoneData.formatted);
      } else {
        // En este caso, mantenemos el valor para que el usuario lo pueda corregir
        this.$set(this.signer, 'phone', formattedNumber);
      }
    },
    validationEval() {
      this.focusValidation = true;
    },
    onRestrictSignChange() {
      if (!this.signer.restrictSign) {
        this.signer.rfc = '';
      }
    },
    onRestrictSelectorChange() {
      this.signer.rfc = '';
    },
    onInputChange(value) {
      if (value.length < 3) {
        return;
      }
      if (value.trim() === '') {
        this.emailSuggestions = [];
      } else {
        this.debouncedSearch(value);
      }
    },
    searchSigners(query) {
      this.$store.dispatch("document/searchSigners", {query}).then(
        response => {
          console.log(response)
          this.emailSuggestions = response.data
        }
      ).catch(error => {
        console.log(error)
      })
    },
    onSelectSuggestion(selected) {
      this.$store.dispatch("document/searchSignersByEmail", {email: selected}).then(
        response => {
          console.log(response);
          this.signer.name = response.data[0].attributes.name;
          this.signer.email = response.data[0].attributes.email;

          // Si el firmante tiene teléfono, lo asignamos también
          if (response.data[0].attributes.phone) {
            this.phoneDisplay = response.data[0].attributes.phone;
            this.signer.phone = response.data[0].attributes.phone;
          }
        }
      )
    }
  },
};
</script>

<style scoped>
.text-danger {
  color: red;
  font-size: 0.8rem;
}
.drag-handle {
  cursor: move;
}
.signer-card {
  transition: all 0.2s ease;
}

.drag-handle {
  cursor: move;
}

.signer-card.dragging {
  opacity: 0.8;
  transform: scale(1.02);
}

.signer-header {
  align-items: center;
}

.signer-order {
  margin-left: 10px;
}

/* Estilos específicos para vue-tel-input */
.vue-tel-input-custom {
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: inherit;
  width: 100%;
}

.vue-tel-input-custom.vue-tel-input-focused {
  border-color: #4173FF;
  box-shadow: 0 0 0 0.2rem rgba(65, 115, 255, 0.25);
}

.vue-tel-input-custom .input-error {
  border-color: #e74c3c;
  box-shadow: 0 0 0 0.2rem rgba(231, 76, 60, 0.25);
}

.phone-input-container {
  margin-bottom: 0.5rem;
}

/* Mejoras para el dropdown de países */
:deep(.vti__dropdown) {
  border-radius: 4px 0 0 4px;
  transition: all 0.3s ease;
}

:deep(.vti__dropdown:hover) {
  background-color: #f8f9fa;
}

:deep(.vti__dropdown-list) {
  max-height: 240px;
  overflow-y: auto;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e4e8;
}

:deep(.vti__dropdown-list-item) {
  padding: 10px 10px;
  transition: background-color 0.2s;
}

:deep(.vti__dropdown-list-item:hover) {
  background-color: #f1f5f9;
}

:deep(.vti__dropdown-list-item.highlighted) {
  background-color: #e6f0ff;
}
</style>
